import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { DownloadSimple, UploadSimple, FileXls, CheckCircle, XCircle, WarningCircle, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportEmployeesDialog({ open, onOpenChange, onImported, language }) {
  const T = (ar, en) => (language === 'ar' ? ar : en);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const resp = await axios.get(`${API}/employees/import/template`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employees_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(T('تم تحميل القالب', 'Template downloaded'));
    } catch {
      toast.error(T('فشل تحميل القالب', 'Download failed'));
    }
  };

  const handleExportAll = async () => {
    try {
      const resp = await axios.get(`${API}/employees/export/all`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `employees_full_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(T('تم تصدير بيانات الموظفين كاملة', 'Full employees data exported'));
    } catch {
      toast.error(T('فشل التصدير', 'Export failed'));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/employees/import`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReport(data);
      const s = data.summary;
      if (s.created + s.updated > 0) {
        toast.success(T(
          `تم استيراد ${s.created + s.updated} موظف (${s.created} جديد، ${s.updated} تحديث)`,
          `Imported ${s.created + s.updated} employees (${s.created} new, ${s.updated} updated)`
        ));
        if (onImported) onImported();
      }
      if (s.errors > 0) {
        toast.warning(T(`${s.errors} صف به أخطاء`, `${s.errors} rows had errors`));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || T('فشل الاستيراد', 'Import failed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const copyPasswords = () => {
    if (!report?.created?.length) return;
    const txt = report.created.map(c => `${c.name} | ${c.email} | ${c.password}`).join('\n');
    navigator.clipboard.writeText(txt).then(() => {
      toast.success(T('تم نسخ كلمات المرور', 'Passwords copied'));
    });
  };

  const handleClose = () => {
    setReport(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] flex flex-col" data-testid="import-employees-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
            <FileXls size={24} weight="duotone" />
            {T('استيراد الموظفين من Excel', 'Import Employees from Excel')}
          </DialogTitle>
          <DialogDescription>
            {T('قم بتنزيل القالب أولاً، عبئه ثم ارفعه. الصفوف الموجودة سيتم تحديثها، والجديدة ستُضاف بكلمة مرور عشوائية.',
               'Download the template first, fill it in, then upload it. Existing rows will be updated; new rows will be added with random passwords.')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          {/* Steps */}
          {!report && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-white border border-[#C4BFF0] rounded-lg p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#655dc6] text-white flex items-center justify-center text-sm font-bold">1</div>
                  <p className="text-sm font-semibold text-[#655dc6]">{T('تنزيل القالب', 'Download Template')}</p>
                </div>
                <p className="text-xs text-[#655dc6]/60">
                  {T('قالب Excel به جميع الأعمدة مع تعليمات.', 'Excel template with all columns and instructions.')}
                </p>
                <Button onClick={handleDownloadTemplate} variant="outline" size="sm"
                  className="border-[#655dc6] text-[#655dc6] hover:bg-[#E8E6F5] gap-2 w-full" data-testid="download-template-btn">
                  <DownloadSimple size={16} weight="bold" />
                  {T('تنزيل قالب فارغ', 'Empty Template')}
                </Button>
                <Button onClick={handleExportAll} variant="outline" size="sm"
                  className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 gap-2 w-full" data-testid="export-all-btn">
                  <DownloadSimple size={16} weight="bold" />
                  {T('تصدير بيانات الموظفين الحالية', 'Export Current Data')}
                </Button>
              </div>

              <div className="bg-white border border-[#C4BFF0] rounded-lg p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#655dc6] text-white flex items-center justify-center text-sm font-bold">2</div>
                  <p className="text-sm font-semibold text-[#655dc6]">{T('رفع الملف المعبأ', 'Upload Filled File')}</p>
                </div>
                <p className="text-xs text-[#655dc6]/60">
                  {T('سيتم التحقق من البيانات وعرض تقرير شامل.', 'Data will be validated and a full report shown.')}
                </p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange}
                  className="hidden" data-testid="import-file-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2 w-full" data-testid="upload-file-btn">
                  <UploadSimple size={16} weight="bold" />
                  {uploading ? T('جارٍ الرفع...', 'Uploading...') : T('اختيار ملف Excel', 'Choose Excel File')}
                </Button>
              </div>
            </div>
          )}

          {/* Report */}
          {report && (
            <div className="space-y-3" data-testid="import-report">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-xs text-green-700">{T('جديد', 'Created')}</p>
                  <p className="text-2xl font-bold text-green-700" data-testid="report-created">{report.summary.created}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xs text-blue-700">{T('تحديث', 'Updated')}</p>
                  <p className="text-2xl font-bold text-blue-700" data-testid="report-updated">{report.summary.updated}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <p className="text-xs text-red-700">{T('أخطاء', 'Errors')}</p>
                  <p className="text-2xl font-bold text-red-700" data-testid="report-errors">{report.summary.errors}</p>
                </div>
              </div>

              {report.created?.length > 0 && (
                <div className="bg-white border border-[#C4BFF0] rounded-lg p-3" data-testid="created-section">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-[#655dc6] flex items-center gap-2">
                      <CheckCircle size={18} weight="duotone" className="text-green-600" />
                      {T('موظفون جدد + كلمات المرور', 'New Employees + Passwords')}
                    </p>
                    <Button variant="ghost" size="sm" onClick={copyPasswords} className="text-[#655dc6] gap-1 h-7" data-testid="copy-passwords-btn">
                      <Copy size={14} />
                      <span className="text-xs">{T('نسخ', 'Copy')}</span>
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {report.created.map((c, i) => (
                      <div key={i} className="grid grid-cols-3 gap-2 text-xs bg-[#F2F0FA] rounded p-2 items-center">
                        <span className="text-[#655dc6] font-medium truncate">{c.name}</span>
                        <span className="text-[#655dc6]/70 truncate" dir="ltr">{c.email}</span>
                        <code className="text-[#655dc6] font-mono bg-white px-2 py-1 rounded border border-[#C4BFF0] text-center" dir="ltr">{c.password}</code>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.updated?.length > 0 && (
                <div className="bg-white border border-[#C4BFF0] rounded-lg p-3">
                  <p className="text-sm font-semibold text-[#655dc6] flex items-center gap-2 mb-2">
                    <WarningCircle size={18} weight="duotone" className="text-blue-600" />
                    {T('موظفون تم تحديثهم', 'Updated Employees')}
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {report.updated.map((u, i) => (
                      <div key={i} className="text-xs flex justify-between gap-2 bg-blue-50/50 rounded p-2">
                        <span className="text-[#655dc6]">{u.name}</span>
                        <span className="text-[#655dc6]/60" dir="ltr">{u.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.errors?.length > 0 && (
                <div className="bg-white border border-red-200 rounded-lg p-3" data-testid="errors-section">
                  <p className="text-sm font-semibold text-red-600 flex items-center gap-2 mb-2">
                    <XCircle size={18} weight="duotone" />
                    {T('صفوف بها أخطاء', 'Rows with Errors')}
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {report.errors.map((e, i) => (
                      <div key={i} className="text-xs flex gap-2 bg-red-50 rounded p-2">
                        <span className="text-red-700 font-mono shrink-0">{T('صف', 'Row')} {e.row}:</span>
                        <span className="text-red-700">{e.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-3 pt-3 border-t border-[#C4BFF0]/30 shrink-0">
          {report && (
            <Button variant="outline" onClick={() => setReport(null)} data-testid="import-another-btn">
              {T('استيراد ملف آخر', 'Import Another')}
            </Button>
          )}
          <Button variant={report ? 'default' : 'outline'} onClick={handleClose}
            className={report ? 'bg-[#655dc6] hover:bg-[#544cb0]' : ''} data-testid="close-import-btn">
            {report ? T('تم', 'Done') : T('إلغاء', 'Cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
